import React, { useState, useEffect, useMemo, memo } from 'react';
import { Box, Text, VStack, Button, HStack } from '@chakra-ui/react';
import { ViewIcon, CheckCircleIcon, TimeIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { USER_TYPES } from '../constants';
import { sendLog, sendError, getCachedBusinessEvents, convertTimestampToTimeString } from '../utils';

// Memoized EventItem to prevent unnecessary re-renders
const EventItem = memo(({ event, onShowInfluencers, navigate }: any) => {
  const viewCount = event.viewCount || 0;
  const pending = event.pending || 0;
  const approved = event.approved || 0;

  return (
    <Box
      width="100%"
      maxWidth="326px"
      borderRadius="20px"
      overflow="hidden"
      backgroundColor="#120F1B"
      border="1px solid"
      borderColor="linear-gradient(134deg, rgba(29, 27, 38, 1) 0%, rgba(107, 99, 140, 1) 50%, rgba(29, 27, 38, 1) 100%)"
      boxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
      cursor="pointer"
      onClick={() => navigate(`/event/${event.id}`, { state: { fromBusiness: true } })}
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "0px 6px 8px 0px rgba(0, 0, 0, 0.35)"
      }}
      transition="all 0.2s ease-in-out"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      {/* Info Area */}
      <Box
        padding="17px 0px 0px 11.97px"
        flex="1"
      >
        {/* Event Name */}
        <Text
          className="static-text-big-white"
          marginBottom="8px"                
        >
          {event.name}
        </Text>

        {/* Date and Time */}
        <Text
          className="static-text"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {event.date} | {event.time}
        </Text>

        {/* Location */}
        <Text
          className="static-text"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {event.location}
        </Text>

        {/* Stats Row */}
        <HStack
          spacing="20px"
          marginTop="12px"
          marginBottom="12px"
        >
          {/* Viewed */}
          <VStack spacing="1px" align="flex-start">
            <HStack spacing="4px">
              <ViewIcon boxSize={4} color="#90909A" />
              <Text className="static-text" color="#90909A">
                {viewCount}
              </Text>
            </HStack>
            <Text className="static-text" color="#90909A">
              Viewed
            </Text>
          </VStack>

          {/* Approved */}
          <VStack spacing="1px" align="flex-start">
            <HStack spacing="4px">
              <CheckCircleIcon boxSize={4} color="#4ade80" />
              <Text className="static-text" color="#4ade80">
                {approved}
              </Text>
            </HStack>
            <Text className="static-text" color="#4ade80">
              Approved
            </Text>
          </VStack>

          {/* Pending */}
          <VStack spacing="1px" align="flex-start">
            <HStack spacing="4px">
              <TimeIcon boxSize={4} color="#fb923c" />
              <Text className="static-text" color="#fb923c">
                {pending}
              </Text>
            </HStack>
            <Text className="static-text" color="#fb923c">
              Pending
            </Text>
          </VStack>
        </HStack>
      </Box>

      {/* Buttons Row */}
      <HStack
        spacing="8px"
        padding="0 11.97px 12px 11.97px"
      >
        {/* Details Button */}
        <Button
          width="101px"
          height="36px"
          borderRadius="48px"
          fontSize="14px"
          fontWeight="500"
          lineHeight="1.14"
          padding="0"
          minW="auto"
          backgroundColor="#1D1D1D"
          color="#FFFFFF"
          _hover={{
            backgroundColor: "#2A2A2A"
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/event/${event.id}`, { state: { fromBusiness: true } });
          }}
        >
          Details
        </Button>

        {/* Influencers Button */}
        <Button
          width="101px"
          height="36px"
          borderRadius="48px"
          fontSize="14px"
          fontWeight="500"
          lineHeight="1.14"
          padding="0"
          minW="auto"
          backgroundColor="#C6D959"
          color="#0C1301"
          _hover={{
            backgroundColor: "#b8cc4a"
          }}
          onClick={(e) => {
            e.stopPropagation();
            onShowInfluencers(event.id, event.name);
          }}
        >
          Influencers
        </Button>
      </HStack>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if event data actually changed
  return (
    prevProps.event.id === nextProps.event.id &&
    prevProps.event.image === nextProps.event.image &&
    prevProps.event.name === nextProps.event.name &&
    prevProps.event.date === nextProps.event.date &&
    prevProps.event.time === nextProps.event.time &&
    prevProps.event.location === nextProps.event.location &&
    prevProps.event.viewCount === nextProps.event.viewCount &&
    prevProps.event.pending === nextProps.event.pending &&
    prevProps.event.approved === nextProps.event.approved
  );
});

EventItem.displayName = 'EventItem';


interface EventsListProps {
  onShowInfluencers: (eventId: string, eventName: string) => void;
}

const EventsList: React.FC<EventsListProps> = ({ onShowInfluencers }) => {
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [rawEventsData, setRawEventsData] = useState<any[]>([]);

  // Fetch events data (only once, not dependent on selectedCategory)
  useEffect(() => {
    const fetchEvents = async () => {
      if (!config?.apiUrl || !user?.id || user.userType !== USER_TYPES.BUSINESS) {
        return;
      }

      setIsLoading(true);
      try {
        sendLog('BusinessEventsListFetchAttempt', {
          businessId: user.id
        });

        // Use cached events or fetch from API (no category filter - backend returns all events for business)
        const eventsData = await getCachedBusinessEvents(config.apiUrl, user.id);
        console.log('BusinessEventsList received events:', {
          count: eventsData.length,
          events: eventsData.map((e: any) => ({
            id: e.id,
            eventName: e.eventName,
            category: e.category
          }))
        });
        setRawEventsData(eventsData);
        
        sendLog('BusinessEventsListLoadSuccess', {
          businessId: user.id,
          totalEvents: eventsData.length,
          note: 'All events loaded for business'
        });
      } catch (error: any) {
        const errorToLog = error instanceof Error 
          ? error 
          : new Error(error.message || 'Failed to fetch events');
        
        sendError(errorToLog, {
          context: 'BusinessEventsList.fetchEvents',
          businessId: user.id,
          status: error.response?.status
        });
        
        setRawEventsData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [config?.apiUrl, user?.id, user?.userType]);

  // Transform events - memoized to prevent re-renders (no category filtering)
  const transformedEvents = useMemo(() => {
    if (rawEventsData.length === 0) {
      return [];
    }

    // Log events loaded
    if (rawEventsData.length > 0) {
      sendLog('BusinessEventsLoaded', {
        totalEvents: rawEventsData.length
      });
    }

    // Transform API response to match component's expected format (all events, no filtering)
    return rawEventsData.map((event: any) => {
          // Extract date from startTime timestamp
          let parsedDate: Date | null = null;
          let dateString = '';
          
          if (event.startTime) {
            parsedDate = new Date(event.startTime);
            if (!isNaN(parsedDate.getTime())) {
              dateString = parsedDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              });
            }
          }

          // Read timestamp from top-level field and convert to time string
          const timeString = convertTimestampToTimeString(event.startTime);
          
          return {
            id: event.id,
            name: event.eventName,
            date: dateString,
            time: timeString,
            location: event.data?.eventLocation || event.eventLocation || '',
            day: parsedDate && !isNaN(parsedDate.getTime()) 
              ? parsedDate.toLocaleDateString('en-US', { weekday: 'short' }) 
              : '',
            viewCount: (event.data?.viewedByInfluencers?.length) || 0,
            pending: event.applicationStats?.pending || 0,
            approved: event.applicationStats?.approved || 0
          };
        });
  }, [rawEventsData]);

  return (
    <Box
      position="absolute"
      top="161px"
      left="24px"
      right="24px"
      bottom="98px"
      overflowY="auto"
      overflowX="hidden"
      zIndex={2}
    >
      <VStack spacing="11px" align="stretch">
        {isLoading ? (
          <Box textAlign="center" padding="40px">
            <Text className="static-text">Loading events...</Text>
          </Box>
        ) : transformedEvents.length === 0 ? (
          <Box textAlign="center" padding="40px">
            <Text className="static-text">No events found</Text>
          </Box>
        ) : (
          transformedEvents.map((event) => (
            <EventItem
              key={event.id}
              event={event}
              onShowInfluencers={onShowInfluencers}
              navigate={navigate}
            />
          ))
        )}
      </VStack>
    </Box>
  );
};

export default EventsList;
